<script>
    import { IS_DEBUG_BUILD } from "Util/util";
    import { BROWSER_AUTOCOMPLETE_TOKEN, isValidAutocompleteToken } from "Util/browserAutocompleteConstants";

    export default {
        inject: {
            validationHub: { default: null }
        },
        props: {
            // Value and mutation behavior props
            modelValue: { type: String, required: false, default: null },
            lazy: { type: Boolean, required: false, default: false },
            externalNativeHandlers: { type: Object, required: false, default: null },

            // Invisible identification props
            id: { type: String, required: false, default: null },
            name: { type: String, required: false, default: null },
            dataTestRole: { type: String, required: false, default: null },
            // Used to indicate to autocomplete that the input is part of a group of related inputs
            groupId: { type: String, required: false, default: null },

            // Input behavior and appearance props
            disabled: { type: Boolean, required: false, default: false },
            readonly: { type: Boolean, required: false, default: false },
            autocomplete: { type: String, required: false, default: null },
            alignTextRight: { type: Boolean, required: false, default: false },
            placeholder: { type: String, required: false, default: null },

            // Label props
            label: { type: String, required: false, default: null },
            required: { type: Boolean, required: false, default: false },
            showHelp: { type: Boolean, required: false, default: false },
            helpLink: { type: String, required: false, default: null },
            altLabel: { type: String, required: false, default: null },
            isSubLabel: { type: Boolean, required: false, default: false },
            boldLabel: { type: Boolean, required: false, default: false },
            italicLabel: { type: Boolean, required: false, default: false },

            // Validation props
            validator: { type: Object, required: false, default: null },
            validationEnabled: { type: Boolean, required: false, default: true },
            externalValidationResult: { type: Object, required: false, default: null },
            showValidationErrors: { type: Boolean, required: false, default: true },
            immediateValidation: { type: Boolean, required: false, default: false },
            validationGroupId: { type: String, required: false, default: null }
        },
        emits: ["update:modelValue", "validated"],
        data: function () {
            const _id = this.id || this.$idGenerator.getNextId("pcInputBase");
            return {
                internalName: this.name,
                /**
                 * tracks if we have validators. Having validators is not the same as having validation enabled.
                 * there are cases where we want to setup validators, but disable validation until some external event
                 * occurs (e.g. user tries to submit form)
                 */
                validationResult: null,
                internalId: _id,
                skipEmit: false,
                /**
                 * internal value that gets set after the first change event or when validate() is called.
                 * this is used to track if it is the right time to do validation
                 * (provided that validation is enabled, but that is tracked seperately in validationEnabled).
                 */
                doValidation: false,
                validated: false,
                internalType: null, //override if necessary
                overrideGetNewValueFn: null, //override if necessary
                /**
                 * Internal. Track if the current input has focus
                 */
                hasFocus: false,
                internalValidationGroupId: this.validationGroupId ?? this.$idGenerator.getNextId("validationGroup")
            };
        },
        computed: {
            hasValidator() {
                return this.validator !== null && this.validator !== undefined;
            },
            labelBinder() {
                return {
                    label: this.label,
                    forId: this.internalId,
                    muted: this.isSubLabel,
                    italic: this.isSubLabel || this.italicLabel,
                    showRequired: this.required,
                    showHelp: this.showHelp,
                    helpLink: this.helpLink,
                    bold: this.boldLabel
                };
            },
            overrideInputClasses() {
                return null; //override in extending components.
            },
            inputClasses() {
                let overrideInputClasses = {};
                if (this.overrideInputClasses && typeof this.overrideInputClasses === "object") {
                    overrideInputClasses = this.overrideInputClasses;
                }
                return {
                    "is-invalid": this.hasValidationError,
                    "text-end": this.alignTextRight,
                    ...overrideInputClasses
                };
            },
            hasValidationError() {
                return Boolean(this.validationError);
            },
            validationError() {
                if (this.externalValidationResult) {
                    return this.externalValidationResult.error;
                }
                if (this.validationResult) {
                    return this.validationResult.error;
                }
                return null;
            },
            effectiveAltLabel() {
                return this.altLabel || this.placeholder;
            },
            internalValue: {
                get() {
                    // Only default to empty string if value is actually null or
                    // undefined. 0 and false could be valid values so don't
                    // revert to "" in those cases.
                    return this.modelValue != null ? this.modelValue : "";
                },
                set(value) {
                    if (!this.lazy) {
                        this.emitChange(value);
                    }
                }
            },
            // The structure of a valid autocomplete attribute value is explained here:
            // https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete#description
            // The autocomplete attribute can be a space-separated list of tokens. If the input is part
            // of a group of inputs such as a contact, the first token in all of is the "section" identifier,
            // which tells the browser which inputs to associate with each other. The subsequent tokens can
            // give hints about what type of input to expect, which helps the browser populate the group from
            // info saved in the user's browser
            internalAutocomplete() {
                // Special case; if we're explicitly turning off autocomplete, use a dummy value depending on
                // whether the element has focus. This is a bit of a hack, but it's the only way to prevent
                // the browser from autofilling the input, since Chrome explicitly ignores the accepted value of "off".
                // We only use the dummy value when the element has focus to avoid having a static, invalid value
                // that could hurt the accessibility of the input.
                if (this.autocomplete === BROWSER_AUTOCOMPLETE_TOKEN.off) {
                    return this.hasFocus ? "block-autocomplete" : this.autocomplete;
                }

                // Including only a "section-*" token will be flagged as an invalid use of the autocomplete attribute.
                if (!this.autocomplete) {
                    return null;
                }

                const tokens = this.autocomplete.split(/\s+/);
                if (tokens.some((t) => !isValidAutocompleteToken(t))) {
                    throw `Invalid autocomplete value: ${this.autocomplete}`;
                }

                // Prepend group token if present
                if (this.groupId) {
                    tokens.unshift(`section-${this.groupId}`);
                }

                return tokens.join(" ");
            },
            /** normally, we would bind local properties and data values to a child component using
             * v-bind: <propName> = "<localValue>".
             * however, we can also bind a whole bunch of properties at once by omitting the propName
             * and having the localValue be an object. For example,
             * v-bind = "binder" where this.binder = {a:this.foo, b: this.bar, c:this.biz}
             * is completely equivalent to
             * v-bind:a="foo"
             * v-bind:b="bar"
             * v-bind:c="biz"
             *
             * This property lets us setup a whole bunch of common bindings with a sigle line in the template.
             */
            binder() {
                const binderObject = {
                    class: this.inputClasses,
                    type: this.internalType,
                    name: this.internalName,
                    disabled: this.disabled,
                    readonly: this.readonly,
                    id: this.internalId,
                    placeholder: this.placeholder,
                    tabindex: 0,
                    autocomplete: this.internalAutocomplete,
                    ["data-test-role"]: this.dataTestRole
                };

                if (!this.label && this.effectiveAltLabel) {
                    binderObject["aria-label"] = this.effectiveAltLabel;
                }
                return binderObject;
            },
            listeners() {
                return {
                    change: this.onChange,
                    blur: this.onBlur,
                    focusin: this.onFocusIn,
                    ...this.externalNativeHandlers
                };
            },
            validationErrorId() {
                return `${this.internalValidationGroupId}-validation-error`;
            }
        },
        watch: {
            validationEnabled(value) {
                if (value) {
                    this.validate();
                } else {
                    this.validationResult = null;
                }
            }
        },
        created() {
            if (this.immediateValidation) {
                this.validate();
            }
            if (this.validationHub) {
                this.validationHub.register(this.internalId, this);
            }
            this._dbgWarnMissingLabels();
            this._dbgWarnKeyupHandlerAndValidator();
        },
        beforeUnmount() {
            if (this.validationHub) {
                this.validationHub.unregister(this.internalId);
            }
        },
        methods: {
            emitChange(value) {
                // validation and input events have to be matched
                // (ie. validation can only happen when we emit "input").
                // otherwise any re-render caused by the validation event
                // (e.g. showing a new error) will make the value disappear
                this._validate(value);
                this.$emit("update:modelValue", value);
                if (this.afterEmitValue) {
                    this.afterEmitValue(value);
                }
            },
            _validate(value) {
                if (this.hasValidator && this.doValidation && this.validationEnabled) {
                    this.validationResult = this.validator.validate(value);
                    this.$emit("validated", this.validationResult);
                }
            },
            validate() {
                this._setDoValidationTrue();
                this._validate(this.modelValue);
            },
            clearValidation() {
                if (this.hasValidator && this.validationEnabled) {
                    this.validationResult = null;
                    this.$emit("validated", this.validationResult);
                }
            },
            _setDoValidationTrue() {
                if (this.validationEnabled) {
                    this.doValidation = true;
                }
            },
            async onChange(event) {
                let newVal = event.target.value;

                if (this.overrideGetNewValueFn) {
                    newVal = this.overrideGetNewValueFn(event.target);
                }

                this._setDoValidationTrue();

                this._validate(newVal);
                if (this.lazy) {
                    this.emitChange(newVal);
                }

                if (this.afterOnChange) {
                    await this.$nextTick();
                    this.afterOnChange();
                }
            },
            onBlur() {
                this.hasFocus = false;
                // this assumes that we already have the value set.
                // because of that, we need to wait until the next tick to call validate.
                this.$nextTick(() => this.validate());
            },
            focusIn(select) {
                if (this.$refs.input) {
                    this.$refs.input.focus();
                    if (select) {
                        this.$refs.input.select();
                    }
                }
            },
            onFocusIn() {
                this.hasFocus = true;
            },
            _dbgWarnKeyupHandlerAndValidator() {
                if (!IS_DEBUG_BUILD) {
                    return;
                }

                if (!this.validator) {
                    return;
                }

                // If there's a keyup native handler and a validator, immediate
                // validation must be enabled or there may be cases where the
                // keyup handler fires before validation has occurred for the
                // first time. This happens, for instance, in IE11
                if (this.externalNativeHandlers && this.externalNativeHandlers.keyup && !this.immediateValidation) {
                    // eslint-disable-next-line max-len
                    const errorMsg = `Input ${this.internalId} has a native keyup handler and a validator but does not have immediate validation enabled. Enable immediate validation to avoid unexpected behavior in IE11`;
                    console.error(errorMsg);
                }
            },
            _dbgWarnMissingLabels() {
                if (!IS_DEBUG_BUILD) {
                    return;
                }

                this.$nextTick(() => {
                    let hasLabels = false;
                    if (this.$refs.input && this.$refs.input.labels) {
                        hasLabels = this.$refs.input.labels.length > 0;
                    }
                    if (!this.label && !this.effectiveAltLabel && !hasLabels) {
                        this.$app.registerAccessibilityViolation(
                            `inputBase element must have a label or altLabel. id:${this.internalId}`
                        );
                    }
                });
            }
        }
    };
</script>
